/**
 * The insert surface for §5.1's closed set — and it is closed here too.
 *
 * There is no "add component" affordance and no plugin slot, because the cap is
 * the feature: ADR 0002 puts it as "an open component system breaks
 * round-tripping, and once round-tripping breaks, markdown becomes a lossy
 * export and AI legibility degrades without anyone noticing." A toolbar that can
 * only produce seven things is the visible half of that guarantee; the schema is
 * the enforcing half.
 *
 * Everything inserted comes from `insertions.ts`, so the round-trip test and
 * these buttons cannot disagree about what a table or a callout is.
 */

import {
  CALLOUT_VARIANTS,
  type CalloutVariant,
  LIVE_DATA_FORMATS,
  type LiveDataFormat,
  type NodeJSON,
} from '@matchbox/shared/markdown';
import type { JSONContent } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import {
  AlertCircle,
  Bold01,
  Code01,
  CodeSquare01,
  Dotpoints01,
  Heading01,
  Heading02,
  Image01,
  Italic01,
  Link01,
  LinkBroken01,
  List,
  Monitor01,
  Strikethrough01,
  Table,
  Zap,
} from '@untitledui/icons';
import { useState } from 'react';
import { Button } from '../components/ui/button.tsx';
import { cn } from '../components/ui/cn.ts';
import { Dialog, DialogBody, DialogFooter } from '../components/ui/dialog.tsx';
import { Select, TextField } from '../components/ui/field.tsx';
import {
  calloutNode,
  codeBlockNode,
  embedNode,
  imageNode,
  liveDataNode,
  tableNode,
  taskListNode,
} from './insertions.ts';

interface ToolProps {
  label: string;
  icon: typeof Bold01;
  onPress: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
}

function Tool({ label, icon: Icon, onPress, isActive, isDisabled }: ToolProps) {
  return (
    <Button
      variant="tertiary"
      size="sm"
      onPress={onPress}
      isDisabled={isDisabled}
      aria-label={label}
      aria-pressed={isActive}
      className={cn('px-2', isActive && 'bg-tertiary text-primary')}
    >
      <Icon className="size-4" />
    </Button>
  );
}

function Divider() {
  return <span aria-hidden="true" className="mx-1 h-5 w-px bg-[var(--border-color-secondary)]" />;
}

/** Which dialog is open, if any — each needs a value the document cannot guess. */
type Prompt = 'link' | 'image' | 'embed' | 'liveData' | null;

export interface DocumentToolbarProps {
  editor: Editor;
  /** False while the socket is down: inserting into a document nobody is saving. */
  isDisabled?: boolean;
}

export function DocumentToolbar({ editor, isDisabled }: DocumentToolbarProps) {
  const [prompt, setPrompt] = useState<Prompt>(null);

  /**
   * `useEditor` does not re-render on transactions in Tiptap 3 — that is the
   * legacy behaviour, off by default — so a button that read `editor.isActive()`
   * during render would show the state of whatever the last unrelated render
   * happened to catch. `useEditorState` subscribes to exactly these flags and
   * re-renders when one of them changes, which is also why the whole toolbar is
   * not repainted on every keystroke.
   */
  const active = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance.isActive('bold'),
      italic: instance.isActive('italic'),
      strike: instance.isActive('strike'),
      code: instance.isActive('code'),
      h1: instance.isActive('heading', { level: 1 }),
      h2: instance.isActive('heading', { level: 2 }),
      bulletList: instance.isActive('bulletList'),
      orderedList: instance.isActive('orderedList'),
      taskList: instance.isActive('taskList'),
      codeBlock: instance.isActive('codeBlock'),
      callout: instance.isActive('callout'),
      link: instance.isActive('link'),
      linkHref: String(instance.getAttributes('link').href ?? ''),
    }),
  });

  const insert = (node: NodeJSON) => {
    editor
      .chain()
      .focus()
      .insertContent(node as JSONContent)
      .run();
  };

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      aria-orientation="horizontal"
      className="flex flex-wrap items-center gap-0.5 rounded-lg border border-secondary bg-primary p-1"
    >
      <Tool
        label="Bold"
        icon={Bold01}
        isDisabled={isDisabled}
        isActive={active.bold}
        onPress={() => editor.chain().focus().toggleBold().run()}
      />
      <Tool
        label="Italic"
        icon={Italic01}
        isDisabled={isDisabled}
        isActive={active.italic}
        onPress={() => editor.chain().focus().toggleItalic().run()}
      />
      <Tool
        label="Strikethrough"
        icon={Strikethrough01}
        isDisabled={isDisabled}
        isActive={active.strike}
        onPress={() => editor.chain().focus().toggleStrike().run()}
      />
      <Tool
        label="Inline code"
        icon={Code01}
        isDisabled={isDisabled}
        isActive={active.code}
        onPress={() => editor.chain().focus().toggleCode().run()}
      />

      <Divider />

      <Tool
        label="Heading 1"
        icon={Heading01}
        isDisabled={isDisabled}
        isActive={active.h1}
        onPress={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <Tool
        label="Heading 2"
        icon={Heading02}
        isDisabled={isDisabled}
        isActive={active.h2}
        onPress={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <Tool
        label="Bulleted list"
        icon={Dotpoints01}
        isDisabled={isDisabled}
        isActive={active.bulletList}
        onPress={() => editor.chain().focus().toggleBulletList().run()}
      />
      <Tool
        label="Numbered list"
        icon={List}
        isDisabled={isDisabled}
        isActive={active.orderedList}
        onPress={() => editor.chain().focus().toggleOrderedList().run()}
      />

      <Divider />

      {/* The seven components of §5.1, in the order the spec lists them. */}
      <CalloutMenu editor={editor} isActive={active.callout} isDisabled={isDisabled} />
      <Tool
        label="Embed"
        icon={Monitor01}
        isDisabled={isDisabled}
        onPress={() => setPrompt('embed')}
      />
      <Tool
        label="Table"
        icon={Table}
        isDisabled={isDisabled}
        onPress={() => insert(tableNode())}
      />
      <Tool
        label="Task list"
        icon={List}
        isDisabled={isDisabled}
        isActive={active.taskList}
        onPress={() => insert(taskListNode())}
      />
      <Tool
        label="Code block"
        icon={CodeSquare01}
        isDisabled={isDisabled}
        isActive={active.codeBlock}
        onPress={() => insert(codeBlockNode())}
      />
      <Tool
        label="Image"
        icon={Image01}
        isDisabled={isDisabled}
        onPress={() => setPrompt('image')}
      />
      <Tool
        label="Live data"
        icon={Zap}
        isDisabled={isDisabled}
        onPress={() => setPrompt('liveData')}
      />

      <Divider />

      <Tool
        label="Link"
        icon={Link01}
        isDisabled={isDisabled}
        isActive={active.link}
        onPress={() => setPrompt('link')}
      />
      <Tool
        label="Remove link"
        icon={LinkBroken01}
        isDisabled={isDisabled || !active.link}
        onPress={() => editor.chain().focus().unsetMark('link').run()}
      />

      {prompt === 'link' ? (
        <LinkDialog
          initialHref={active.linkHref}
          onClose={() => setPrompt(null)}
          onSubmit={(href, title) => {
            editor
              .chain()
              .focus()
              .setMark('link', { href, title: title?.trim() ? title.trim() : null })
              .run();
          }}
        />
      ) : null}

      {prompt === 'image' ? (
        <ImageDialog
          onClose={() => setPrompt(null)}
          onSubmit={(src, alt) => insert(imageNode(src, alt))}
        />
      ) : null}

      {prompt === 'embed' ? (
        <EmbedDialog
          onClose={() => setPrompt(null)}
          onSubmit={(url, title) => insert(embedNode(url, title))}
        />
      ) : null}

      {prompt === 'liveData' ? (
        <LiveDataDialog
          onClose={() => setPrompt(null)}
          onSubmit={(source, format) => insert(liveDataNode(source, format))}
        />
      ) : null}
    </div>
  );
}

const CALLOUT_LABELS: Record<CalloutVariant, string> = {
  note: 'Note',
  tip: 'Tip',
  warning: 'Warning',
  danger: 'Danger',
};

/**
 * Four variants, spelled out rather than hidden behind a picker. The set is
 * closed in the schema — "a fifth variant is a schema change" — so a menu that
 * looked extensible would be describing something that is not.
 */
function CalloutMenu({
  editor,
  isActive,
  isDisabled,
}: {
  editor: Editor;
  isActive: boolean;
  isDisabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tool
        label="Callout"
        icon={AlertCircle}
        isDisabled={isDisabled}
        isActive={isActive}
        onPress={() => setOpen(true)}
      />
      {open ? (
        <Dialog
          title="Insert a callout"
          description="Four kinds, and only four — the set is fixed by the schema."
          isOpen
          size="sm"
          onOpenChange={(next) => !next && setOpen(false)}
        >
          {(close) => (
            <DialogBody>
              <div className="flex flex-wrap gap-2">
                {CALLOUT_VARIANTS.map((variant) => (
                  <Button
                    key={variant}
                    variant="secondary"
                    onPress={() => {
                      editor.chain().focus().insertContent(calloutNode(variant)).run();
                      close();
                      setOpen(false);
                    }}
                  >
                    {CALLOUT_LABELS[variant]}
                  </Button>
                ))}
              </div>
            </DialogBody>
          )}
        </Dialog>
      ) : null}
    </>
  );
}

interface PromptDialogProps<T extends unknown[]> {
  onClose: () => void;
  onSubmit: (...args: T) => void;
}

function LinkDialog({
  initialHref,
  onClose,
  onSubmit,
}: PromptDialogProps<[string, string?]> & { initialHref: string }) {
  const [href, setHref] = useState(initialHref);
  const [title, setTitle] = useState('');

  return (
    <Dialog
      title="Link"
      description="A link to somewhere outside Matchbox OS is marked as external wherever it appears — its permissions are whatever that system decided (§4.6)."
      isOpen
      size="sm"
      onOpenChange={(open) => !open && onClose()}
    >
      {(close) => (
        <>
          <DialogBody>
            <TextField label="URL" value={href} onChange={setHref} isRequired autoFocus />
            <TextField label="Title" value={title} onChange={setTitle} />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="tertiary"
              onPress={() => {
                close();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!href.trim()}
              onPress={() => {
                onSubmit(href.trim(), title);
                close();
                onClose();
              }}
            >
              Apply
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}

function ImageDialog({ onClose, onSubmit }: PromptDialogProps<[string, string]>) {
  const [src, setSrc] = useState('');
  const [alt, setAlt] = useState('');

  return (
    <Dialog
      title="Insert an image"
      description="Paste the address of an image. An asset already uploaded to this document is served from /api/blobs/<id>/content."
      isOpen
      size="sm"
      onOpenChange={(open) => !open && onClose()}
    >
      {(close) => (
        <>
          <DialogBody>
            <TextField label="Image URL" value={src} onChange={setSrc} isRequired autoFocus />
            <TextField
              label="Alt text"
              value={alt}
              onChange={setAlt}
              description="What the image says, for anyone who cannot see it."
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="tertiary"
              onPress={() => {
                close();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!src.trim()}
              onPress={() => {
                onSubmit(src.trim(), alt);
                close();
                onClose();
              }}
            >
              Insert
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}

function EmbedDialog({ onClose, onSubmit }: PromptDialogProps<[string, string]>) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');

  return (
    <Dialog
      title="Insert an embed"
      description="A Figma frame, a Loom, a staging URL. It inherits this document's permissions the way an embedded asset does — the embedded site does not."
      isOpen
      size="sm"
      onOpenChange={(open) => !open && onClose()}
    >
      {(close) => (
        <>
          <DialogBody>
            <TextField label="URL" value={url} onChange={setUrl} isRequired autoFocus />
            <TextField label="Title" value={title} onChange={setTitle} />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="tertiary"
              onPress={() => {
                close();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!url.trim()}
              onPress={() => {
                onSubmit(url.trim(), title);
                close();
                onClose();
              }}
            >
              Insert
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}

/**
 * The live-data placeholder.
 *
 * Nothing resolves one in Phase 1 — §13 defers the data behind it — and that is
 * exactly why the syntax is fixed now: §5.1 asks for it to be designed "while
 * nothing uses it, to avoid migrating later". `source` names a business action
 * the way §11.2 names an MCP tool, never a query.
 */
function LiveDataDialog({ onClose, onSubmit }: PromptDialogProps<[string, LiveDataFormat]>) {
  const [source, setSource] = useState('');
  const [format, setFormat] = useState<LiveDataFormat>('value');

  return (
    <Dialog
      title="Insert live data"
      description="A named source, resolved when the document is read. Nothing resolves one yet — the syntax exists now so documents written today do not need migrating later."
      isOpen
      size="sm"
      onOpenChange={(open) => !open && onClose()}
    >
      {(close) => (
        <>
          <DialogBody>
            <TextField
              label="Source"
              value={source}
              onChange={setSource}
              isRequired
              autoFocus
              placeholder="project.time_summary"
              description="A named action, not a query."
            />
            <Select
              label="Format"
              options={LIVE_DATA_FORMATS.map((value) => ({ value, label: value }))}
              selectedKey={format}
              onSelectionChange={setFormat}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              variant="tertiary"
              onPress={() => {
                close();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={!source.trim()}
              onPress={() => {
                onSubmit(source.trim(), format);
                close();
                onClose();
              }}
            >
              Insert
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
